'use client'

import { useCallback, useEffect, useMemo } from 'react'
import confetti from 'canvas-confetti'

import type { RouterOutputs } from '@package/api'
import { Dialog, DialogContent, DialogDescription, DialogTitle, NumberFlow } from '@ui/web/components'
import { calculateGradeBasedOnXp, calculateTotalXpEarned, starOnLessonArray } from '@helper/utils/business-logic'
import { formatDate } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import { RewardStar } from '../components/reward-star'

interface MissionStatusModalProps {
  data: RouterOutputs['lesson']['getEnrollmentDetails']
  open: boolean
  onOpenChange: (open: boolean) => void
  confettiOnOpen?: boolean
}

export function MissionStatusModal({
  data,
  open,
  onOpenChange,
  confettiOnOpen: confettiOnOpenProps,
}: MissionStatusModalProps) {
  const learnProgressData = data.learnProgressData
  const lessonsData = data.lessonsData
  const isSuccess = data.isSuccess
  const confettiOnOpen = confettiOnOpenProps ?? isSuccess

  const fireConfetti = useCallback(() => {
    const count = 200
    const defaults = {
      origin: { y: 0.7 },
      spread: 360,
      ticks: 100,
      gravity: 0,
      decay: 0.94,
      startVelocity: 30,
      zIndex: 2502,
    }

    function fire(particleRatio: number, opts: confetti.Options) {
      void confetti({
        ...defaults,
        ...opts,
        particleCount: Math.floor(count * particleRatio),
      })
    }

    fire(0.25, {
      spread: 26,
      startVelocity: 55,
    })

    fire(0.2, {
      spread: 60,
    })

    fire(0.35, {
      spread: 100,
      decay: 0.91,
      scalar: 0.8,
    })

    fire(0.1, {
      spread: 120,
      startVelocity: 25,
      decay: 0.92,
      scalar: 1.2,
    })

    fire(0.1, {
      spread: 120,
      startVelocity: 45,
    })
  }, [])

  useEffect(() => {
    if (open && confettiOnOpen) {
      fireConfetti()
    }
  }, [open, fireConfetti, confettiOnOpen])

  const thereIsUngradedLesson = useMemo(() => {
    const thereIsInClassLesson = lessonsData.some((lesson) => lesson.type === 'IN_CLASS')

    if (!thereIsInClassLesson) return false

    const isAllCompletedLessonsAndHaveEarnedXp = lessonsData.every((lesson) => {
      const thisLearnProgress = learnProgressData.find((learnProgress) => learnProgress.lessonId === lesson.id)

      if (!thisLearnProgress) return false

      return (
        thisLearnProgress.endedAt && thisLearnProgress.status === 'COMPLETED' && thisLearnProgress.earnedXps.length > 0
      )
    })

    return !isAllCompletedLessonsAndHaveEarnedXp
  }, [lessonsData, learnProgressData])

  const gradedXp = useMemo(() => {
    const lessonsWithWeights = lessonsData.map((lesson) => ({
      id: lesson.id,
      lessonWeights: (lesson.lessonToSkillWeights as { value: number }[]).map((sw) => ({
        calculatedWeight: Number(sw.value),
      })),
    }))

    const resp = calculateGradeBasedOnXp({
      learnProgresses: learnProgressData,
      lessons: lessonsWithWeights,
    })

    return resp.grade
  }, [learnProgressData, lessonsData])

  const stars = useMemo(
    () => starOnLessonArray({ type: 'graded_xp', gradedXp }).filter((s) => s.isFilled).length as 0 | 1 | 2 | 3,
    [gradedXp],
  )

  const Content = () => (
    <>
      <section className='srd:w-full srd:flex srd:flex-col srd:gap-y-6 srd:max-w-[400px] srd:items-center srd:justify-center'>
        {!thereIsUngradedLesson && (
          <section className='srd:flex srd:items-center srd:justify-center srd:shrink-0'>
            <RewardStar stars={stars} className='srd:w-42 srd:sm:w-60' />
          </section>
        )}

        {(() => {
          if (isSuccess) {
            if (thereIsUngradedLesson) {
              return (
                <h6 className='srd:font-bold srd:text-center srd:bg-complementary-purple-20 srd:text-complementary-purple-100 srd:px-5 srd:py-2 srd:rounded-lg srd:w-fit srd:h-fit srd:md:text-4xl srd:text-3xl'>
                  Tunggu nilai...
                </h6>
              )
            }

            return (
              <h6 className='srd:font-bold srd:text-center srd:bg-main-blue-20 srd:text-main-blue-100 srd:px-5 srd:py-2 srd:rounded-lg srd:w-fit srd:h-fit srd:md:text-4xl srd:text-3xl'>
                Misi sukses
              </h6>
            )
          }

          return (
            <h6 className='srd:font-bold srd:text-center srd:bg-main-red-20 srd:text-main-red-100 srd:px-5 srd:py-2 srd:rounded-lg srd:w-fit srd:h-fit srd:md:text-4xl srd:text-3xl'>
              Misi gagal...
            </h6>
          )
        })()}

        <section className='srd:w-fit srd:flex srd:flex-col srd:gap-y-2 srd:h-fit srd:border srd:border-neutrals-white srd:p-2 srd:rounded-xl srd:text-center srd:min-w-[200px]'>
          <p className='srd:text-sm'>Total Poin Diraih</p>

          <NumberFlow
            value={calculateTotalXpEarned(
              learnProgressData.flatMap((learnProgress) =>
                learnProgress.earnedXps.map((earnedXp) => ({ xpEarned: earnedXp.xpEarned })),
              ),
            )}
            className='srd:font-bold srd:md:text-3xl srd:text-2xl'
            format={{ minimumFractionDigits: 0, maximumFractionDigits: 0 }}
          />
        </section>
      </section>

      <section className='srd:w-full srd:flex srd:flex-col srd:gap-y-2'>
        {lessonsData.map((lesson, index) => {
          const thisLearnProgress = learnProgressData.find((learnProgress) => learnProgress.lessonId === lesson.id)

          const progressStatus = thisLearnProgress
            ? thisLearnProgress.endedAt
              ? formatDate(thisLearnProgress.endedAt, { dateStyle: 'long' })
              : 'Belum selesai'
            : 'Belum dimulai'

          return (
            <section
              key={`${lesson.id}-${index}-mission-status-modal`}
              className='srd:w-full srd:bg-neutrals-white srd:rounded-xl srd:p-4 srd:md:grid srd:md:grid-cols-[65%_35%] srd:text-neutrals-black srd:items-center srd:gap-x-4 srd:flex srd:flex-col srd:gap-y-4'
            >
              <section className='srd:md:grid srd:md:grid-cols-[auto_1fr] srd:w-full srd:items-center srd:gap-x-3 srd:flex srd:flex-nowrap'>
                <p className='srd:w-fit srd:font-semibold'>{index + 1}</p>

                <section className='srd:flex srd:items-center srd:gap-x-2 srd:flex-nowrap srd:w-full'>
                  <p>{lesson.title}</p>

                  <p className='srd:px-1 srd:py-0.5 srd:rounded-sm srd:border srd:border-main-blue-50 srd:text-neutrals-n-75 srd:text-sm'>
                    {(() => {
                      if (lesson.type === 'READ') return 'Baca'
                      if (lesson.type === 'DISCOVERY') return 'Eksplorasi'
                      if (lesson.type === 'IN_CLASS') return 'Di Kelas'

                      return 'Quiz'
                    })()}
                  </p>
                </section>
              </section>

              <section className='srd:md:grid srd:md:grid-cols-[2.5fr_1fr] srd:flex srd:items-center srd:justify-between srd:w-full srd:gap-x-4'>
                <p className='srd:text-sm srd:text-center'>{progressStatus}</p>

                {(() => {
                  const earnedXps = thisLearnProgress
                    ? thisLearnProgress.earnedXps.length > 0
                      ? thisLearnProgress.earnedXps.reduce((acc, curr) => acc + curr.xpEarned / 100, 0)
                      : null
                    : null

                  if (earnedXps) {
                    return (
                      <NumberFlow
                        // Part of bug fix yang aneh
                        value={earnedXps * 100}
                        className='srd:font-bold srd:text-xl srd:text-center'
                        format={{ minimumFractionDigits: 0, maximumFractionDigits: 2 }}
                      />
                    )
                  }

                  if (lesson.type === 'IN_CLASS') {
                    return <p className='srd:font-bold srd:text-xl srd:text-center'>???</p>
                  }

                  return (
                    <NumberFlow
                      value={0}
                      className='srd:font-bold srd:text-xl srd:text-center'
                      format={{ minimumFractionDigits: 0, maximumFractionDigits: 2 }}
                    />
                  )
                })()}
              </section>
            </section>
          )
        })}
      </section>
    </>
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'srd:w-full srd:max-w-[800px] srd:bg-linear-to-b srd:from-complementary-purple-100 srd:to-main-blue-100 srd:text-neutrals-white srd:flex srd:flex-col srd:gap-y-10 srd:overflow-y-auto srd:items-center srd:justify-center',
          // Offset the padding due to translation of the star image
          'srd:px-4 srd:pb-4 srd:pt-8',
        )}
      >
        <DialogTitle className='srd:sr-only'>Hasil Belajar</DialogTitle>
        <DialogDescription className='srd:sr-only'>Hasil Belajar</DialogDescription>

        <Content />
      </DialogContent>
    </Dialog>
  )
}
